import React, { useEffect, useState, useCallback } from 'react';
import {
    Button, Card, Col, DatePicker, Row, Select, Space, Table,
    Tag, Typography, Alert, Tooltip, Grid, Badge, Modal, Input,
    Dropdown, Menu, Avatar, message
} from 'antd';
import {
    DownloadOutlined, ReloadOutlined, ShopOutlined,
    SearchOutlined, EyeOutlined, UserOutlined, PhoneOutlined,
    EnvironmentOutlined, CompassOutlined, MoreOutlined,
    ExportOutlined, InboxOutlined, RollbackOutlined, DeleteOutlined,
    UserAddOutlined
} from '@ant-design/icons';
import moment from 'moment';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import MainLayout from '../../components/MainLayout';
import DashboardCards from '../Orders/DashboardCards';
import AsignDriver from '../Orders/AsignDriver';
import MultipleOrdersAssign from '../Orders/MultipleOrdersAssign';
import SegmentedButton from '../Orders/SegmentedButton';
import { HomeContext } from '../Orders/HomeContext';
import styles from '../Orders/Orders.module.css';

import { ApiServices } from '../../services/apiServices';
import { ApiConfig, driversListUrl } from '../../services/ApiConfigs';
import { openMap } from '../../services/helper';
import { getStatusConfig } from '../../utils/orderStatusUtils';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

function escapeCSV(val) {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function exportToCSV(franchiseName, orders) {
    const headers = ['Order ID', 'Customer Name', 'Customer Phone', 'Delivery Address', 'Pincode', 'Amount', 'Payment Type', 'Driver', 'Status', 'Date'];
    const rows = orders.map(o => {
        const addresses = o.orderAddresses || o.OrderAddresses || [];
        const delivery = addresses.find(a => a.addressType === 'Delivery') || {};
        const shipment = (o.shipments || [])[0] || {};
        const driverName = shipment.driver ? `${shipment.driver.firstName || ''} ${shipment.driver.lastName || ''}`.trim() : '';

        return [
            o.orderId,
            o.customerName,
            o.customerPhone,
            `${delivery.line1 || ''} ${delivery.city || ''} ${delivery.state || ''}`.trim() || o.deliveryAddress,
            delivery.zipcode || o.zipcode || '',
            o.amount || o.totalPrice,
            o.paymentType,
            driverName || o.driverName || '',
            o.orderStatus,
            o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '',
        ];
    });

    const csv = [headers, ...rows].map(row => row.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${franchiseName?.replace(/\s+/g, '_') || 'franchise'}_orders_${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function FranchiseDashboard() {
    const screens = useBreakpoint();
    const navigate = useNavigate();
    const isMobile = !screens.md;

    const userDetail = useSelector((res) => res.UserData.userData?.user || res.UserData.userData?.franchise);
    const franchiseInfo = useSelector((res) => res.UserData.userData?.franchise || res.UserData.userData?.user);
    const storeId = userDetail?.storeId;

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateRange, setDateRange] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [asignDriverModalOpen, setAsignDriverModalOpen] = useState(false);
    const [multipleModalOpen, setMultipleModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState({});
    const [activeButton, setActiveButton] = useState('pending_orders');
    const [archiveFilter, setArchiveFilter] = useState('active'); // 'active' | 'archived' | 'all'
    const [exporting, setExporting] = useState(false);

    // Pagination states
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalOrders, setTotalOrders] = useState(0);
    const [counts, setCounts] = useState({});

    // Debounce search text
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchText]);

    // Fetch warehouses and drivers
    useEffect(() => {
        fetchWarehouses();
        fetchDrivers();
    }, [storeId]);

    // Reset page to 1 on filter changes
    useEffect(() => {
        setPage(1);
    }, [debouncedSearchText, statusFilter, dateRange, activeButton, archiveFilter]);

    // Fetch orders when page, pageSize, or filters change
    useEffect(() => {
        if (storeId) {
            fetchOrders();
        }
    }, [storeId, page, pageSize, archiveFilter, statusFilter, dateRange, activeButton, debouncedSearchText]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            if (!storeId) return;

            let url = `${ApiConfig.getallOrderByStoreId}/${storeId}?page=${page}&limit=${pageSize}`;
            if (archiveFilter === 'archived') url += '&archive=1';
            else if (archiveFilter === 'all') url += '&includeArchived=1';

            if (debouncedSearchText) {
                url += `&search=${encodeURIComponent(debouncedSearchText)}`;
            }

            if (statusFilter !== 'all') {
                url += `&status=${encodeURIComponent(statusFilter)}`;
            }

            if (activeButton) {
                url += `&tab=${encodeURIComponent(activeButton)}`;
            }

            if (dateRange && dateRange[0] && dateRange[1]) {
                url += `&startDate=${dateRange[0].format('YYYY-MM-DD')}&endDate=${dateRange[1].format('YYYY-MM-DD')}`;
            }

            const todayStart = moment().startOf('day').toISOString();
            const todayEnd = moment().endOf('day').toISOString();
            url += `&todayStart=${encodeURIComponent(todayStart)}&todayEnd=${encodeURIComponent(todayEnd)}`;

            const response = await ApiServices('get', url);
            if (response) {
                let ordersData = [];
                if (response.pagination) {
                    ordersData = Array.isArray(response.data) ? response.data : [];
                    setOrders(ordersData);
                    setTotalOrders(response.pagination.total);
                } else {
                    ordersData = Array.isArray(response.data) ? response.data : [];
                    setOrders(ordersData);
                    setTotalOrders(ordersData.length);
                }
                if (response.counts) {
                    setCounts(response.counts);
                }
            }
        } catch (error) {
            console.error('Error fetching franchise orders:', error);
            message.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchWarehouses = async () => {
        try {
            const response = await ApiServices('get', ApiConfig.WAREHOUSE);
            setWarehouses(response?.data || []);
        } catch (error) {
            console.error('Error fetching warehouses:', error);
        }
    };

    const fetchDrivers = async () => {
        try {
            if (!storeId) return;
            const response = await ApiServices('get', driversListUrl(storeId));
            setDrivers(response?.data || []);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        }
    };

    const handleArchiveOrder = async (orderId, e) => {
        if (e) e.stopPropagation();
        try {
            const res = await ApiServices('put', `${ApiConfig.ORDERS_UPDATE_STATUS}/${orderId}/archive`);
            if (res?.success || res?.message === 'Order archived') {
                message.success('Order moved to archive');
                fetchOrders();
            } else {
                message.error(res?.message || 'Failed to archive order');
            }
        } catch (err) {
            message.error(err?.message || 'Failed to archive order');
        }
    };

    const handleUnarchiveOrder = async (orderId, e) => {
        if (e) e.stopPropagation();
        try {
            const res = await ApiServices('put', `${ApiConfig.ORDERS_UPDATE_STATUS}/${orderId}/unarchive`);
            if (res?.success || res?.message === 'Order restored from archive') {
                message.success('Order restored from archive');
                fetchOrders();
            } else {
                message.error(res?.message || 'Failed to restore order');
            }
        } catch (err) {
            message.error(err?.message || 'Failed to restore order');
        }
    };

    const handleDeleteOrder = (record, e) => {
        if (e) e.stopPropagation();
        Modal.confirm({
            title: 'Delete order permanently?',
            content: (
                <>
                    Order <strong>{record.orderId}</strong> and related delivery records will be deleted. This cannot be undone.
                </>
            ),
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const res = await ApiServices('delete', `${ApiConfig.ORDERS_UPDATE_STATUS}/${record.id}`);
                    if (res?.success || res?.message?.toLowerCase().includes('deleted')) {
                        message.success('Order deleted');
                        fetchOrders();
                    } else {
                        message.error(res?.message || 'Failed to delete order');
                    }
                } catch (err) {
                    message.error(err?.message || 'Failed to delete order');
                }
            },
        });
    };

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            let url = `${ApiConfig.getallOrderByStoreId}/${storeId}?page=1&limit=500`;
            if (debouncedSearchText) url += `&search=${encodeURIComponent(debouncedSearchText)}`;
            if (statusFilter !== 'all') url += `&status=${encodeURIComponent(statusFilter)}`;
            if (activeButton) url += `&tab=${encodeURIComponent(activeButton)}`;
            if (dateRange && dateRange[0] && dateRange[1]) {
                url += `&startDate=${dateRange[0].format('YYYY-MM-DD')}&endDate=${dateRange[1].format('YYYY-MM-DD')}`;
            }

            const response = await ApiServices('get', url);
            const exportData = response?.data || orders;
            exportToCSV(franchiseInfo?.name, exportData);
        } catch (e) {
            message.error(e?.message || 'Export error');
        } finally {
            setExporting(false);
        }
    };

    const hasDriverAssigned = (order) => {
        const shipments = order.shipments || [];
        return shipments.some(s => s.driverId);
    };

    const getWarehouseName = (warehouseId) => {
        const warehouse = warehouses.find(w => w.id === warehouseId);
        return warehouse?.name || 'N/A';
    };

    const getDriverName = (driverId) => {
        const driver = drivers.find(d => d.id === driverId);
        return driver ? `${driver.firstName} ${driver.lastName}` : 'Not Assigned';
    };

    const getDeliveryAddress = (order) => {
        const addresses = order.orderAddresses || order.OrderAddresses || [];
        const delivery = addresses.find(addr => addr.addressType === 'Delivery');
        if (!delivery) return 'N/A';
        return `${delivery.city || ''}, ${delivery.state || ''} ${delivery.zipcode || ''}`.trim();
    };

    const columns = [
        {
            title: 'Order ID',
            dataIndex: 'orderId',
            key: 'orderId',
            width: 140,
            fixed: 'left',
            sorter: (a, b) => String(a?.orderId || '').localeCompare(String(b?.orderId || ''), undefined, { numeric: true, sensitivity: 'base' }),
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    <Space size={4}>
                        <Text strong style={{ fontSize: 13 }}>{text || 'N/A'}</Text>
                        {record.archivedAt && (
                            <Tag color="default" style={{ fontSize: 10, padding: '0 4px', lineHeight: 1.4 }}>Archived</Tag>
                        )}
                        {record.platformType && (
                            <Tag color="purple" style={{ fontSize: 10, padding: '0 4px', lineHeight: 1.4 }}>
                                {String(record.platformType).charAt(0).toUpperCase() + record.platformType.slice(1)}
                            </Tag>
                        )}
                    </Space>
                    {((record.shipments || [])[0]?.trackingNumber || record.trackingNumber) && (
                        <Text type="secondary" style={{ fontSize: 11 }}>Track: {(record.shipments || [])[0]?.trackingNumber || record.trackingNumber}</Text>
                    )}
                    {!record.trackingNumber && record.shopifyOrderGid && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {record.shopifyOrderGid.split('/').pop()}
                        </Text>
                    )}
                    {record.custom_location_id && (
                        <Tag color="cyan" style={{ fontSize: 10, padding: '0 4px', lineHeight: 1.4, marginTop: 2, display: 'inline-block' }}>
                            Loc: {record.custom_location_id}
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Customer',
            key: 'customer',
            width: 160,
            responsive: ['sm', 'md', 'lg', 'xl'],
            sorter: (a, b) => String(a?.customerName || '').localeCompare(String(b?.customerName || ''), undefined, { sensitivity: 'base' }),
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Space>
                        <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                        <Text strong>{record.customerName || 'N/A'}</Text>
                    </Space>
                    {record.customerPhone && (
                        <Space size={4}>
                            <PhoneOutlined style={{ fontSize: 11, color: '#52c41a' }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>{record.customerPhone}</Text>
                        </Space>
                    )}
                </Space>
            ),
        },
        {
            title: 'Address & Pincode',
            key: 'deliveryAddress',
            width: 170,
            responsive: ['sm', 'md', 'lg', 'xl'],
            render: (_, record) => {
                const addresses = record.orderAddresses || record.OrderAddresses || [];
                const delivery = addresses.find(addr => addr.addressType === 'Delivery');
                const address = getDeliveryAddress(record);
                return (
                    <Space direction="vertical" size={2}>
                        <Tooltip title={address}>
                            <Space size={4}>
                                <EnvironmentOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                                <Text ellipsis style={{ maxWidth: 150 }}>{address}</Text>
                            </Space>
                        </Tooltip>
                        {delivery?.zipcode && (
                            <Tag color="geekblue" style={{ fontSize: 11, fontWeight: 600, margin: 0, padding: '1px 6px', borderRadius: 4 }}>
                                {delivery.zipcode}
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Warehouse',
            key: 'warehouse',
            width: 120,
            responsive: ['md', 'lg', 'xl'],
            render: (_, record) => {
                const shipment = (record.shipments || []).find(s => s.pickupWarehouseId);
                const whId = shipment?.pickupWarehouseId || record.warehouseId;
                return (
                    <Space>
                        <ShopOutlined style={{ color: '#722ed1' }} />
                        <Text>{getWarehouseName(whId)}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Driver',
            key: 'driver',
            width: 130,
            responsive: ['sm', 'md', 'lg', 'xl'],
            render: (_, record) => {
                const shipment = (record.shipments || []).find(s => s.driverId);
                const driverId = shipment?.driverId || record.driverId;
                const canAssign = !record.archivedAt && !hasDriverAssigned(record);
                return (
                    <Space>
                        {driverId ? (
                            <>
                                <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#52c41a' }} />
                                <Text>{getDriverName(driverId)}</Text>
                            </>
                        ) : (
                            <Space size={6}>
                                <Tag color="default">Not Assigned</Tag>
                                {canAssign && (
                                    <Tooltip title="Assign driver to this order">
                                        <Button
                                            size="small"
                                            type="text"
                                            icon={<UserAddOutlined style={{ color: '#1677ff' }} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedOrder(record);
                                                setAsignDriverModalOpen(true);
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Space>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Status',
            key: 'status',
            width: 140,
            responsive: ['sm', 'md', 'lg', 'xl'],
            render: (_, record) => {
                const status = record.orderStatus || record.deliveryStatus || record.status || 'PENDING';
                const config = getStatusConfig(status);
                return (
                    <Tag
                        style={{
                            padding: '3px 10px',
                            borderRadius: 6,
                            border: `1px solid ${config.color}`,
                            backgroundColor: config.bg,
                            color: config.textColor || config.color,
                            fontWeight: 500,
                            fontSize: 11,
                        }}
                        icon={config.icon}
                    >
                        {config.text}
                    </Tag>
                );
            },
        },
        {
            title: 'Payment',
            key: 'payment',
            width: 110,
            responsive: ['md', 'lg', 'xl'],
            render: (_, record) => {
                const paymentType = record.paymentType || 'COD';
                const isCOD = paymentType === 'COD';
                return (
                    <Space direction="vertical" size={0}>
                        <Tag color={isCOD ? 'volcano' : 'green'}>{paymentType}</Tag>
                        {record.amount && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                {record.currency_symbol || '₹'} {parseFloat(record.amount || 0).toFixed(2)}
                            </Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 110,
            responsive: ['md', 'lg', 'xl'],
            render: (date) => (
                <Space direction="vertical" size={0}>
                    <Text>{moment(date).format('DD MMM YYYY')}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {moment(date).format('HH:mm A')}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 60,
            fixed: 'right',
            render: (_, record) => (
                <Space onClick={(e) => e.stopPropagation()}>
                    <Dropdown
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="view-details"
                                    icon={<EyeOutlined />}
                                    onClick={() => navigate(`/Orders/${record.id}`)}
                                >
                                    View Details
                                </Menu.Item>
                                <Menu.Item
                                    key="view-map"
                                    icon={<EnvironmentOutlined />}
                                    onClick={(e) => {
                                        e.domEvent?.stopPropagation();
                                        const addresses = record.orderAddresses || record.OrderAddresses || [];
                                        const delivery = addresses.find(addr => addr.addressType === 'Delivery');
                                        if (delivery?.latitude && delivery?.longitude) {
                                            openMap(delivery.latitude, delivery.longitude);
                                        } else {
                                            message.info('Coordinates not available for this delivery address');
                                        }
                                    }}
                                >
                                    View on Map
                                </Menu.Item>
                                {record.archivedAt ? (
                                    <Menu.Item
                                        key="unarchive"
                                        icon={<RollbackOutlined />}
                                        onClick={(e) => { e.domEvent?.stopPropagation(); handleUnarchiveOrder(record.id, e.domEvent); }}
                                    >
                                        Restore from archive
                                    </Menu.Item>
                                ) : (
                                    <Menu.Item
                                        key="archive"
                                        icon={<InboxOutlined />}
                                        onClick={(e) => { e.domEvent?.stopPropagation(); handleArchiveOrder(record.id, e.domEvent); }}
                                    >
                                        Archive order
                                    </Menu.Item>
                                )}
                                <Menu.Divider />
                                <Menu.Item
                                    key="delete"
                                    icon={<DeleteOutlined />}
                                    danger
                                    onClick={(e) => { e.domEvent?.stopPropagation(); handleDeleteOrder(record, e.domEvent); }}
                                >
                                    Delete order
                                </Menu.Item>
                            </Menu>
                        }
                        trigger={['click']}
                    >
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    const statusFilters = [
        { value: 'all', label: 'All', count: counts.all || 0 },
        { value: 'PENDING', label: 'Pending', count: counts.PENDING || 0 },
        { value: 'WAITING_FOR_DRIVER_ACCEPT', label: 'Waiting for Driver Accept', count: counts.WAITING_FOR_DRIVER_ACCEPT || 0 },
        { value: 'READY_FOR_PICKUP', label: 'Ready', count: counts.READY_FOR_PICKUP || 0 },
        { value: 'PARTIAL_READY_TO_PICKUP', label: 'Partial Ready', count: counts.PARTIAL_READY_TO_PICKUP || 0 },
        { value: 'IN_TRANSIT', label: 'In Transit', count: counts.IN_TRANSIT || 0 },
        { value: 'PARTIAL_IN_TRANSIT', label: 'Partial In Transit', count: counts.PARTIAL_IN_TRANSIT || 0 },
        { value: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', count: counts.OUT_FOR_DELIVERY || 0 },
        { value: 'DELIVERED', label: 'Delivered', count: counts.DELIVERED || 0 },
        { value: 'PARTIAL_DELIVERED', label: 'Partial Delivered', count: counts.PARTIAL_DELIVERED || 0 },
        { value: 'COMPLETED', label: 'Completed', count: counts.COMPLETED || 0 },
        { value: 'CLOSED', label: 'Closed', count: counts.CLOSED || 0 },
        { value: 'CANCELLED', label: 'Cancelled', count: counts.CANCELLED || 0 },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        getCheckboxProps: (record) => ({
            disabled: record.archivedAt || hasDriverAssigned(record),
        }),
    };

    const contextValue = {
        asignDriverModalOpen,
        setAsignDriverModalOpen,
        submitAsignDriver: () => {},
        wareHouseData: warehouses,
        driverData: drivers,
        selectedOrder,
        activeButton,
        setActiveButton,
        getOrders: fetchOrders,
        getAssignOrders: () => {},
        multipleModalOpen,
        setMultipleModalOpen,
        awaitingOrders: orders.filter((order) => !hasDriverAssigned(order)),
        selectedRowKeys,
        setSelectedRowKeys,
        orders,
        userDetail,
    };

    return (
        <HomeContext.Provider value={contextValue}>
            <MainLayout>
                <div style={{ width: '100%', maxWidth: 1400, margin: '0 auto' }}>
                    {/* Hero Franchise Info Banner */}
                    <div style={{
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                        borderRadius: 14,
                        padding: isMobile ? '16px' : '22px 28px',
                        marginBottom: 20,
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                    }}>
                        <Row justify="space-between" align="middle" gutter={[16, 16]}>
                            <Col xs={24} md={16}>
                                <h2 style={{ margin: 0, color: '#fff', fontSize: isMobile ? 20 : 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {franchiseInfo?.logo ? (
                                        <Avatar
                                            src={franchiseInfo.logo}
                                            size={isMobile ? 32 : 40}
                                            shape="square"
                                            style={{ borderRadius: 8, background: '#fff', border: '2px solid rgba(255,255,255,0.4)', flexShrink: 0 }}
                                        />
                                    ) : (
                                        <ShopOutlined />
                                    )}
                                    {franchiseInfo?.name || 'Franchise Portal'}
                                </h2>
                                <div style={{ marginTop: 8, color: '#bfdbfe', fontSize: 13, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                    {franchiseInfo?.city && (
                                        <span>{franchiseInfo.city}{franchiseInfo.state ? `, ${franchiseInfo.state}` : ''}</span>
                                    )}
                                    <span>·</span>
                                    {franchiseInfo?.radiusEnabled ? (
                                        <Tag color="success" icon={<CompassOutlined />} style={{ fontWeight: 600 }}>
                                            {franchiseInfo?.deliveryRadiusKm || 10} km Radius Active
                                        </Tag>
                                    ) : (
                                        <Tag color="default" icon={<CompassOutlined />}>
                                            Pincode Coverage Only
                                        </Tag>
                                    )}
                                    {franchiseInfo?.location_id && (
                                        <>
                                            <span>·</span>
                                            <span>Location ID:</span>
                                            <Tag color="purple" style={{ fontSize: 12, margin: 0, fontWeight: 700, padding: '1px 8px' }}>
                                                {franchiseInfo.location_id}
                                            </Tag>
                                        </>
                                    )}
                                    <span>·</span>
                                    <span>Assigned Pincode:</span>
                                    <Tag color="cyan" style={{ fontSize: 12, margin: 0, fontWeight: 700, padding: '1px 8px' }}>
                                        {(franchiseInfo?.pincodes && franchiseInfo.pincodes.length > 0) ? franchiseInfo.pincodes[0] : (franchiseInfo?.pincode || '—')}
                                    </Tag>
                                </div>
                            </Col>
                            <Col xs={24} md={8} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                                <Space wrap>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={fetchOrders}
                                        loading={loading}
                                        style={{ borderRadius: 8, background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none' }}
                                    >
                                        Refresh
                                    </Button>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        onClick={handleExportCSV}
                                        loading={exporting}
                                        style={{ borderRadius: 8, background: '#fff', color: '#1e3a8a', fontWeight: 600, border: 'none' }}
                                    >
                                        Export CSV
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </div>

                    {/* Admin-grade Order Dashboard Cards */}
                    <div style={{ marginBottom: 16 }}>
                        <DashboardCards
                            orders={counts.all || 0}
                            todayOrders={counts.today || 0}
                            assignedOrders={counts.assigned || 0}
                            CompleteOrder={counts.completed || 0}
                        />
                    </div>

                    {/* Filters & Segmented Order Tabs Card */}
                    <Card
                        style={{ borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 16 }}
                        bodyStyle={{ padding: isMobile ? 12 : 16 }}
                    >
                        <Row gutter={[12, 10]} align="middle" wrap>
                            <Col xs={24} sm={24} md={8} lg={8} xl={6}>
                                <Search
                                    placeholder="Order ID, Customer, Phone, Tracking"
                                    allowClear
                                    enterButton={<SearchOutlined />}
                                    size="middle"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    style={{ width: '100%' }}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={4} lg={3}>
                                <Select
                                    value={archiveFilter}
                                    onChange={setArchiveFilter}
                                    style={{ width: '100%' }}
                                    size="middle"
                                    options={[
                                        { value: 'active', label: 'Active' },
                                        { value: 'archived', label: 'Archived' },
                                        { value: 'all', label: 'All' },
                                    ]}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={5} lg={4}>
                                <Select value={statusFilter} onChange={setStatusFilter} style={{ width: '100%' }} size="middle">
                                    {statusFilters.map(f => (
                                        <Option key={f.value} value={f.value}>{f.label} ({f.count})</Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={8} md={7} lg={6}>
                                <RangePicker size="middle" value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Col>
                            {selectedRowKeys.length > 0 && (
                                <Col xs={24} sm={24} md={6} lg={5} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                                    <Button
                                        type="primary"
                                        icon={<ExportOutlined />}
                                        onClick={() => setMultipleModalOpen(true)}
                                        style={{ borderRadius: 8, fontWeight: 600 }}
                                    >
                                        Assign Driver ({selectedRowKeys.length})
                                    </Button>
                                </Col>
                            )}
                        </Row>
                        <div style={{ marginTop: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <SegmentedButton activeButton={activeButton} setActiveButton={setActiveButton} />
                        </div>
                    </Card>

                    {/* Orders Table Card */}
                    <Card
                        style={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
                        bodyStyle={{ padding: 0 }}
                    >
                        <Table
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={orders}
                            loading={loading}
                            rowKey="id"
                            scroll={{ x: 'max-content' }}
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: totalOrders,
                                showSizeChanger: true,
                                showTotal: (total) => `${total} franchise orders`,
                                size: 'small',
                                onChange: (p, ps) => {
                                    setPage(p);
                                    setPageSize(ps);
                                }
                            }}
                            onRow={(record) => ({
                                onClick: () => navigate(`/Orders/${record.id}`),
                                style: { cursor: 'pointer' },
                            })}
                        />
                    </Card>

                    {/* Modals for Driver Assignment */}
                    <AsignDriver />
                    <MultipleOrdersAssign
                        open={multipleModalOpen}
                        onClose={() => { setMultipleModalOpen(false); setSelectedRowKeys([]); }}
                        selectedRowKeys={selectedRowKeys}
                        onSuccess={fetchOrders}
                    />
                </div>
            </MainLayout>
        </HomeContext.Provider>
    );
}
